/**
 * What the player must be TOLD about a mission's special rules (03 §2, 10 §1). The sim enforces them
 * silently; every one of these lines exists because a rule the player cannot see is a bug.
 * Pure: text from the mission definition and the objective state, nothing else - and driven by the
 * rule data (kind, effect, the rule's own words), never by which archetype this is.
 *
 *   ordersLines / keyOrdersLine   the standing orders, in full and folded to one line (missionOrders.ts)
 *   consequenceLine   whatever is counting down or in progress right now (alert consequence first)
 *   alertCountdownLine / timerLine   its two halves, which the objective panel shows on separate lines
 *   progressText, completeText, uselessChargeText, timedEventText, blackoutText   banners
 */

import type { MissionDef, SimEvent, World } from '../../sim/types';
import { TICK_RATE } from '../../sim/types';
import { chargesLine } from './missionCharges';
import { ruleOfType as rule } from './missionOrders';
import { progressLine } from './missionProgress';
import { ALERT_COUNTDOWN, mmss, TIMED_BANNER, TIMED_COUNTDOWN, TIMED_WINDOW } from './missionRuleText';

export { keyOrdersLine, ordersBlock, ordersLine, ordersLines } from './missionOrders';
export type { OrdersLine } from './missionOrders';

const seconds = (ticks: number): number => Math.ceil(ticks / TICK_RATE);

/** An alert consequence is counting down: what it is and how long is left. Null when none is. */
export function alertCountdownLine(def: MissionDef, world: World): string | null {
  for (const c of world.objective.consequences) {
    if (c.spent || c.ticks <= 0) continue;
    const r = def.objective.rules[c.rule];
    if (r?.type !== 'alertConsequence') continue;
    const text = r.warning ?? ALERT_COUNTDOWN[r.effect];
    if (text !== null) return `${text} ${mmss(seconds(c.ticks))}`;
  }
  return null;
}

/** Metres from the ranger to the nearest point a strike will land on; null when the mission names none. */
function strikeDistance(def: MissionDef, world: World, ids: readonly number[] | undefined): number | null {
  const points = ids ?? (def.objective.clearFromPropId !== undefined ? [def.objective.clearFromPropId] : []);
  let best: number | null = null;
  for (const id of points) {
    const prop = def.props.find((p) => p.id === id);
    if (!prop) continue;
    const d = Math.sqrt((prop.x - world.ranger.x) ** 2 + (prop.z - world.ranger.z) ** 2);
    if (best === null || d < best) best = d;
  }
  return best;
}

/** "GET 80 M CLEAR - 37 M": how far he has to be, and how far he is. */
export function clearanceText(def: MissionDef, world: World, ids?: readonly number[]): string | null {
  const need = def.objective.clearDistance ?? 0;
  const have = strikeDistance(def, world, ids);
  if (need <= 0 || have === null) return null;
  return have >= need ? `${need} M CLEAR — HOLD THERE` : `GET ${need} M CLEAR — ${Math.floor(have)} M`;
}

/**
 * The soonest timed event still pending, or the window that is open now; failing both, what a count that has
 * not started is waiting for (`from: 'zoneEntered'`). `countdown`: it is a pending clock, which a hold shares its line with.
 */
function timedLine(def: MissionDef, world: World): { text: string; countdown: boolean } | null {
  let best: { ticks: number; text: string } | null = null;
  let waiting: string | null = null;
  for (const st of world.objective.timed) {
    const r = def.objective.rules[st.rule];
    if (r?.type !== 'timedEvent') continue;
    if (st.fired) {
      if (st.windowTicks <= 0) continue;
      return { text: `${TIMED_WINDOW[r.effect]} — ${mmss(seconds(st.windowTicks))} LEFT`, countdown: false };
    }
    if (st.ticks < 0 && r.from === 'zoneEntered') waiting ??= r.waiting ?? 'IT STARTS WHEN YOU REACH THE ZONE';
    if (st.ticks <= 0 || (best !== null && st.ticks >= best.ticks)) continue;
    let text = `${r.warning ?? TIMED_COUNTDOWN[r.effect]} ${mmss(seconds(st.ticks))}`;
    if (r.effect === 'strikeLands') text += ` — ${clearanceText(def, world, r.strikePropIds) ?? 'GET CLEAR'}`;
    best = { ticks: st.ticks, text };
  }
  if (best !== null) return { text: best.text, countdown: true };
  return waiting === null ? null : { text: waiting, countdown: false };
}

/**
 * The mission's own clock and work in progress: what he is in the middle of (a mark, a hold), else the
 * soonest timed event, else where the objective stands (the item is in pod A, he is down - search him).
 * A pending timed event is on this line from the moment the ranger is on the ground.
 */
export function timerLine(def: MissionDef, world: World): string | null {
  if (world.objective.failed || world.insertion.phase !== 'ground') return null;
  const working = progressLine(def, world, 'working');
  const timed = timedLine(def, world);
  // A zone being held against the clock shows both: what is left of the hold, and when the next wave lands.
  const shared = working !== null && timed?.countdown === true && def.objective.kind === 'holdZone';
  if (working !== null) return shared ? `${working} — ${timed.text}` : working;
  return timed?.text ?? chargesLine(def, world) ?? progressLine(def, world, 'standing');
}

/** Whatever is counting down right now, most urgent first: an alert consequence, else the mission's own clock. */
export function consequenceLine(def: MissionDef, world: World): string | null {
  return alertCountdownLine(def, world) ?? timerLine(def, world);
}

/** Progress worth a banner, worded for what the mission counts. Null: say nothing until it is complete. */
export function progressText(def: MissionDef, done: number, total: number): string | null {
  switch (def.objective.kind) {
    case 'destroyTargets': return `TARGET DESTROYED ${done}/${total}`;
    case 'freeAndEscort': return `ABOARD ${done}/${total}`;
    case 'useObjects': return total > 1 ? `DONE ${done}/${total}` : null;
    case 'plantAtTargets': return `CHARGE LAID ${done}/${total}`;
    case 'visitZones': return total > 1 ? `POINT MARKED ${done}/${total}` : null;
    // The quest item announces itself ("RADIO SET TAKEN"): a second banner would only repeat it.
    default: return null;
  }
}

export function completeText(def: MissionDef): string {
  if (rule(def, 'bombsHoldUntilMidnight')) {
    return def.objective.kind === 'plantAtTargets' ? 'EVERY CHARGE IS LAID — NOW GET OUT UNSEEN'
      : 'CHARGE SET FOR MIDNIGHT — NOW GET OUT UNSEEN';
  }
  const rules = def.objective.rules;
  const strike = rules.some((r) => r.type === 'timedEvent' && r.effect === 'strikeLands' && r.from === 'complete');
  if (strike) return `OBJECTIVE COMPLETE — THE STRIKE IS COMING: GET ${def.objective.clearDistance ?? 0} M CLEAR`;
  const fetched = rules.some((r) => r.type === 'timedEvent' && r.effect === 'ospreyArrives' && r.from === 'complete');
  if (fetched) return 'OBJECTIVE COMPLETE — THE OSPREY IS COMING';
  return 'OBJECTIVE COMPLETE';
}

/** A time bomb was armed just now on a mission where bombs never go off in play, and it did not complete it. */
export function uselessChargeText(def: MissionDef, world: World): string | null {
  if (!rule(def, 'bombsHoldUntilMidnight') || world.objective.complete || world.objective.failed) return null;
  if (def.objective.kind !== 'plantAtTargets') return 'THAT CHARGE IS NOT AT THE FUEL DUMP — IT WILL DO NOTHING';
  // On a several-target mission the counter has just gone up if the charge was well placed; this is only for one that was not.
  return 'THAT CHARGE IS NOT AT A MARKED TARGET — IT WILL DO NOTHING';
}

type TimedEventFact = Extract<SimEvent, { type: 'timedEvent' }>;

/** The banner for a timed event: it happened, it was brought forward, or its window has shut. Null: say nothing. */
export function timedEventText(def: MissionDef, ev: TimedEventFact): { text: string; bad: boolean } | null {
  const r = def.objective.rules[ev.rule];
  const words = r?.type === 'timedEvent' && r.warning !== undefined ? r.warning : TIMED_COUNTDOWN[ev.effect];
  if (ev.phase === 'hastened') return { text: `THE ALARM HAS HURRIED THEM — ${words} ${mmss(ev.seconds)}`, bad: true };
  if (ev.phase === 'windowClosed') return ev.effect === 'convoyCrosses' ? { text: 'THE CONVOY HAS PASSED', bad: false } : null;
  const text = TIMED_BANNER[ev.effect];
  return text === null ? null : { text, bad: ev.effect !== 'contactArrives' && ev.effect !== 'convoyCrosses' };
}

/** The banner when a blackout group goes dead (10 §2, missions 16 and 33). */
export function blackoutText(lights: number, guns: number): string {
  const parts: string[] = [];
  if (lights > 0) parts.push(`${lights} SEARCHLIGHT${lights === 1 ? '' : 'S'}`);
  if (guns > 0) parts.push(`${guns} AUTOMATED GUN${guns === 1 ? '' : 'S'}`);
  return parts.length === 0 ? 'POWER CUT' : `POWER CUT — ${parts.join(' AND ')} DEAD`;
}
